import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Clock, RefreshCw, MessageCircle } from 'lucide-react';
import type { ActivityUpdateEntry } from '../../../services/podTodayApi';
import { getDayFeed } from '../../../services/podTodayApi';
import './DayFeedDrawer.css';

interface DayFeedDrawerProps {
  planId: number;
  date: string;
  refreshKey?: number; // Increment to force refresh
}

function timeAgo(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const typeIcon: Record<string, React.ReactNode> = {
  late: <Clock size={12} />,
  change: <RefreshCw size={12} />,
  note: <MessageCircle size={12} />,
};

const DayFeedDrawer: React.FC<DayFeedDrawerProps> = ({ planId, date, refreshKey }) => {
  const [expanded, setExpanded] = useState(false);
  const [entries, setEntries] = useState<ActivityUpdateEntry[]>([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    getDayFeed(planId, date)
      .then(data => { setEntries(data.entries); setCount(data.count); })
      .catch(() => {});
  }, [planId, date, refreshKey]);

  if (count === 0) return null;

  return (
    <div className="dfd-container">
      <button className="dfd-toggle" onClick={() => setExpanded(!expanded)}>
        <span className="dfd-count">{count} update{count !== 1 ? 's' : ''} today</span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {expanded && (
        <div className="dfd-entries">
          {entries.map(entry => (
            <div key={entry.id} className={`dfd-entry dfd-entry--${entry.type}`}>
              <span className="dfd-entry-icon">{typeIcon[entry.type]}</span>
              <div className="dfd-entry-content">
                <span className="dfd-entry-text">
                  <strong>{entry.user_name}</strong>
                  {entry.type === 'late' && ` is running ${entry.duration_minutes}m late for `}
                  {entry.type === 'change' && ` changed `}
                  {entry.type === 'note' && ` on `}
                  <strong>{entry.activity_name}</strong>
                  {entry.text && entry.type !== 'late' && `: ${entry.text}`}
                  {entry.text && entry.type === 'late' && entry.text !== `Running ${entry.duration_minutes} min late` && ` — ${entry.text}`}
                </span>
                <span className="dfd-entry-time">{timeAgo(entry.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DayFeedDrawer;
